
import React from "react";
import { Section, i18nManager } from "../i18n";
import { useSearchParams } from "react-router-dom";
import { CacheManager, Schemes, Utils } from "../utils";
import { IProfile, JanusGeminusService, SanitizedResponse } from "../services";
import { Button, FormControl, InputManageRef, Paginator, Popover, PopoverManageRef, Scheme, TabPane, TabPanel, TextInput, Transition } from "@rivtn/noseur";

function Dashboard() {
    const labels = i18nManager.Labels.dashboard;
    const sections = i18nManager.Labels.sections;
    const sectionsMenuItems = Object.keys(sections);
    const profileButton = React.useRef<HTMLButtonElement>(null);
    const janusGeminusService = JanusGeminusService.getInstance();
    const searchInputManageRef = React.useRef<InputManageRef>(null);
    const profilePopOverManageRef = React.useRef<PopoverManageRef>(null);
    const linkCopiedPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const selectedTabIndex = sectionsMenuItems.indexOf(searchParams.get("section") ?? "0") + 1;
    const [profile, setProfile] = React.useState<IProfile>(CacheManager.get(CacheManager.PROFILE_KEY));
    const [filteredSectionData, setFilteredSectionData] = React.useState<Section[]>([]);

    React.useEffect(() => {
        janusGeminusService.getProfile().then(({ apiResponse }: SanitizedResponse<IProfile>) => {
            setProfile(apiResponse.data!);
        }).catch(JanusGeminusService.reportError);
    }, []);

    React.useEffect(() => {
        const searchValue = searchParams.get("search") ?? "";
        setFilteredSectionData(Object.keys(sections).reduce((acc: Section[], key: string) => {
            const selectedSection = searchParams.get("section");
            if (selectedSection && key !== selectedSection) return acc;
            const sectionData = sections[key].filter((section: any) => section.name.toLowerCase().includes(searchValue.toLowerCase()));
            return acc.concat(sectionData);
        }, []));
    }, [searchParams]);

    return (<div className="dashboard">
        <div className="header">
            <div className="title">
                <div className="icon">
                    <div /><div />
                </div>
                <span>{i18nManager.Labels.rivtn}</span>
            </div>
            <FormControl rightContent={<Button scheme={Schemes.RIVTN_INTERNAL_LOCATIONS} text={labels.go}
                style={{ padding: "8px 20px", marginRight: 8 }} onClick={() => searchSectionData()}/>}
                className="search-control" borderless>
                <TextInput defaultValue={searchParams.has("search") ? searchParams.get("search")! : ""}
                    manageRef={searchInputManageRef} onInputComplete={() => searchSectionData()} fill />
            </FormControl>
            <Button leftIcon={<img className="profile-pic" alt={profile?.first_name} src={profile?.profile_picture_location} />} ref={profileButton}
                scheme={Scheme.LIGHT} style={{ fontSize: 20 }} onClick={(e: any) => profilePopOverManageRef.current?.toggle(e, profileButton.current!)} textOnly rounded />
        </div>
        <TabPane renderActiveTabOnly scheme={Schemes.RIVTN_INTERNAL_LOCATIONS} onTabActive={onTabActive} activeIndex={selectedTabIndex} style={{ overflow: "auto", flex: 1 }}>
            {[labels.all].concat(sectionsMenuItems).map((name) => (<TabPanel key={name} header={name} className="sections" fill>
                {filteredSectionData.map((section, itmp) => (<div key={itmp} className="section" onClick={() => gotoLink(section.location)}>
                    <img alt={section.name} src={section.icon} />
                    <span className="name">{section.name}</span>
                    <span className="desc">{section.desc}</span>
                    <span className="link" onClick={(e: any) => copyLink(e, section.location)}>{section.location}</span>
                </div>))}
            </TabPanel>))}
        </TabPane>
        <Paginator style={{ justifyContent: "flex-end", margin: 40 }}/>

        <Popover manageRef={profilePopOverManageRef} className="profile-popover">
            <div className="dt">
                <span className="name">{profile?.first_name} {profile?.last_name}</span>
                <span className="email">{profile?.email}</span>
            </div>
            <span onClick={() => janusGeminusService.backToLogin()}>{labels.logout}</span>
        </Popover>
        <Popover transition={Transition.FADE} manageRef={linkCopiedPopOverManageRef} className="link-copied-popover" pointingArrowClassName="">
            <i className="fa fa-link"/> {labels.link_copied}
        </Popover>
    </div>);

    function onTabActive(index: number) {
        if (index === 0) {
            Utils.updateSearchParams("section", undefined, setSearchParams);
            return;
        }
        Utils.updateSearchParams("section", sectionsMenuItems[index - 1], setSearchParams);
    }
    
    function searchSectionData(queryString?: string) {
        const searchValue = (queryString ?? searchInputManageRef.current?.value() ?? "");
        Utils.updateSearchParams("search", searchValue, setSearchParams);
    }

    function copyLink(e: any, link: string) {
        Utils.copyToClipboard(link, (err) => {
            if (!err) {
                linkCopiedPopOverManageRef.current?.show(e);
                setTimeout(() => linkCopiedPopOverManageRef.current?.hide(), 500);
            } else {
                console.error(err);
            }
        });
    }

    function gotoLink(link: string) {
        window.open(link, '_blank')?.focus();
    }

}

export default Dashboard;
